
import React from "react";
import { SwapRequest } from "@/services/myReagents";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import SwapOfferDetails from "./SwapOfferDetails";
import { CheckCircle, XCircle, Edit, Trash2 } from "lucide-react";

interface SwapRequestCardProps {
  request: SwapRequest;
  isPending: boolean;
  isSentByUser?: boolean;
  onApprove?: (request: SwapRequest) => void;
  onReject?: (request: SwapRequest) => void;
  onEdit?: (request: SwapRequest) => void;
  onDelete?: (request: SwapRequest) => void;
}

const SwapRequestCard: React.FC<SwapRequestCardProps> = ({ 
  request, 
  isPending,
  isSentByUser = false,
  onApprove,
  onReject,
  onEdit,
  onDelete
}) => {
  // For debugging purposes
  console.log("SwapRequestCard rendering:", {
    requestId: request.id,
    status: request.status,
    creditOffer: request.credit_offer,
    offeredReagents: request.offered_reagents,
    offeredReagentId: request.offered_reagent_id,
    offeredReagent: request.offered_reagent,
    isPending
  });
  
  // Get status badge color
  const getStatusBadge = () => {
    switch (request.status) {
      case "pending":
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Pending</Badge>;
      case "approved":
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Approved</Badge>;
      case "rejected":
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Rejected</Badge>;
      case "cancelled":
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Cancelled</Badge>;
      case "completed":
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Completed</Badge>;
      default:
        return <Badge variant="outline">{request.status}</Badge>;
    }
  };
  
  // Format date
  const getFormattedDate = () => {
    if (!request.created_at) return "Unknown date";
    return formatDistanceToNow(new Date(request.created_at), { addSuffix: true });
  };
  
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg">
            {request.reagents.name}
          </CardTitle>
          {getStatusBadge()}
        </div>
        <div className="text-sm text-muted-foreground">
          {isSentByUser ? (
            <>You requested this reagent {getFormattedDate()}</>
          ) : (
            <>Requested by <span className="font-medium">{request.profiles.username || "Unknown"}</span> {getFormattedDate()}</>
          )}
        </div>
      </CardHeader>
      <CardContent className="pb-3">
        <div className="mb-3">
          <p className="text-sm text-muted-foreground mb-1">
            {isSentByUser ? "Owner" : "Requester"}: {isSentByUser ? "Lab Owner" : request.profiles.username}
          </p>
          {request.profiles.lab && (
            <p className="text-sm text-muted-foreground mb-1">
              Lab: {request.profiles.lab}
            </p>
          )}
          {request.profiles.university && (
            <p className="text-sm text-muted-foreground">
              University: {request.profiles.university}
            </p>
          )}
        </div>
        
        <SwapOfferDetails 
          credit_offer={request.credit_offer}
          offered_reagents={request.offered_reagents}
          offered_reagent={request.offered_reagent}
          isPending={isPending}
        />
      </CardContent>
      
      {isPending && !isSentByUser && onApprove && onReject && (
        <CardFooter className="pt-0 flex gap-2">
          <Button 
            size="sm" 
            className="w-full" 
            onClick={() => onApprove(request)}
            variant="default"
          >
            <CheckCircle className="mr-1 h-4 w-4" /> Approve
          </Button>
          <Button 
            size="sm" 
            className="w-full" 
            onClick={() => onReject(request)}
            variant="outline"
          >
            <XCircle className="mr-1 h-4 w-4" /> Reject
          </Button>
        </CardFooter>
      )}
      
      {isSentByUser && isPending && (
        <CardFooter className="pt-0 flex gap-2">
          {onEdit && (
            <Button 
              size="sm" 
              className="w-full" 
              onClick={() => onEdit(request)}
              variant="outline"
            >
              <Edit className="mr-1 h-4 w-4" /> Edit
            </Button>
          )}
          {onDelete && (
            <Button 
              size="sm" 
              onClick={() => onDelete(request)}
              variant="outline"
              className="w-full text-red-600 border-red-300 hover:bg-red-50"
            >
              <Trash2 className="mr-1 h-4 w-4" /> Cancel
            </Button>
          )}
        </CardFooter>
      )}
      
      {isSentByUser && !isPending && (
        <CardFooter className="pt-0">
          <p className="text-xs text-muted-foreground w-full text-center">
            {request.status === "approved" ? "Your swap request was approved" : 
             request.status === "rejected" ? "Your swap request was rejected" : 
             request.status === "cancelled" ? "You cancelled this swap request" :
             "Swap request completed"}
          </p>
        </CardFooter>
      )}
    </Card>
  );
};

export default SwapRequestCard;
